import { AppHTTPService } from '@/api/http-service'
import { useRulesContext } from '@/contexts/RulesContext'
import { useColumnSelectionContext } from '@flatfile/turntable'
import { useCallback, useState } from 'react'
import { OperationItem, PrepareChatConfig } from './types'

/**
 * Hook for managing operations and rules
 */
function useOperationsController(
  threadId: string | null,
  config?: PrepareChatConfig
) {
  const [operationItems, setOperationItems] = useState<OperationItem[]>([])
  const { pendingRules, setPendingRules, addPendingRules, removeRule } =
    useRulesContext()
  const { setColumnSelections } = useColumnSelectionContext()

  /**
   * Updates the operations list with a new operation
   */
  const updateOperation = useCallback((operation: OperationItem) => {
    setOperationItems((prev) => {
      // Check if operation with this ID already exists
      const existingIndex = prev.findIndex((op) => op.id === operation.id)

      if (existingIndex >= 0) {
        // Update existing operation
        return prev.map((op, index) =>
          index === existingIndex ? { ...op, ...operation } : op
        )
      }

      // Add new operation
      return [...prev, operation]
    })
  }, [])

  /**
   * Adds a revert operation to undo a previous operation
   */
  const addRevertOperation = useCallback(
    async (threadId: string, targetOperationId: string) => {
      try {
        // Create the operation object
        const revertOperation = {
          toolName: 'revert',
          status: 'completed',
          id: `revert-${targetOperationId}`,
          targetOperationId,
          timestamp: Date.now(),
        }

        // Update UI with the new operation
        updateOperation({
          id: revertOperation.id,
          toolName: revertOperation.toolName,
          status: revertOperation.status,
          timestamp: revertOperation.timestamp,
          result: { targetOperationId },
        })

        // Remove the rule
        removeRule(targetOperationId)

        // TODO: OLD Thread Update API
        await AppHTTPService.client.threads.update(threadId, {
          operations: [revertOperation],
        })

        return revertOperation
      } catch (error) {
        console.error('Error adding revert operation:', error)
        throw error
      }
    },
    [config?.threadId, updateOperation, removeRule]
  )

  /**
   * Resets all pending rules
   */
  const resetPendingRules = useCallback(() => {
    setPendingRules([])
  }, [setPendingRules])

  /**
   * Updates rules based on thread state
   */
  const updateRulesFromState = useCallback(
    (rules: any[]) => {
      const newDestinationFields = rules.flatMap(
        (rule) => rule.destinationFields || []
      )
      setPendingRules(rules)
      setColumnSelections(newDestinationFields)
    },
    [setPendingRules, setColumnSelections]
  )

  return {
    operationItems,
    updateOperation,
    addRevertOperation,
    pendingRules,
    resetPendingRules,
    updateRulesFromState,
  }
}

export { useOperationsController }
